import React, {
  FC,
  ReactElement,
  ReactNode,
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import tokenService from "./services/token.service";
import AppConfig from "constants/app";
import { ActionType, getUserPermissions } from "reducers/auth.reducer";
import { useDispatch } from "react-redux";
import DrawerLayout from "components/DrawerLayout";
import ApiService from "services/api.service";
import Account from "types/Account";
import { toast } from "react-toastify";
import { prefetchAppData } from "./prefetch";
import queryClient from "./queryClient";
import { LaudspeakerProvider } from "@laudspeaker/react";
import config, { ONBOARDING_API_KEY_KEY, WS_BASE_URL_KEY } from "config";
import EEWrapper from "EE/EEWraper";
import {
  buildLeadStatsMarketingLoginUrl,
  rememberPostLoginRedirect,
} from "utils/postLoginRedirect";

const AiAssistantWidget = React.lazy(
  () => import("components/AiAssistantWidget")
);
const AiAgents = React.lazy(() => import("pages/AiAgents"));
const Analytics = React.lazy(() => import("pages/Analytics"));
const CampaignLauncher = React.lazy(() => import("pages/CampaignLauncher"));
const CompanySetup = React.lazy(
  () => import("pages/CompanySetup/CompanySetup")
);
const Cor = React.lazy(() => import("pages/Cor"));
const CustomModalSettings = React.lazy(
  () => import("pages/CustomModalSettings")
);
const DataTransfer = React.lazy(() => import("pages/DataTransfer"));
const DataTransferTable = React.lazy(() => import("pages/DataTransferTable"));
const EmailBuilder = React.lazy(() => import("pages/EmailBuilder"));
const EmailSettings = React.lazy(
  () => import("pages/EmailSettings/EmailSettings")
);
const EventTracker = React.lazy(() => import("pages/EventTracker"));
const ExampleWorkflows = React.lazy(() => import("pages/ExampleWorkflows"));
const FlowBuilderv2 = React.lazy(() => import("pages/FlowBuilderv2"));
const FlowViewerv2 = React.lazy(() => import("pages/FlowViewerv2"));
const GroupCreation = React.lazy(() => import("pages/GroupCreation/index"));
const GroupTablev2 = React.lazy(() => import("pages/GroupTablev2"));
const GroupViewer = React.lazy(() => import("pages/GroupViewer"));
const Home = React.lazy(() => import("pages/Home"));
const InAppBackgroundProvider = React.lazy(
  () => import("pages/InAppBuilder/InAppBackgroundProvider")
);
const InAppBuilder = React.lazy(() => import("pages/InAppBuilder"));
const Inbox = React.lazy(() => import("pages/Inbox"));
const InviteConfirmation = React.lazy(() => import("EE/InviteConfirmation"));
const InviteMember = React.lazy(() => import("pages/Settingsv2/InviteMember"));
const JavascriptSnippetSettings = React.lazy(
  () => import("pages/JavascriptSnippetSettings")
);
const LeadStatsAutomationQueue = React.lazy(
  () => import("pages/LeadStatsAutomationQueue")
);
const Login = React.lazy(() => import("./pages/Login"));
const ManualGroupCreator = React.lazy(() => import("pages/ManualGroupCreator"));
const NotificationPreferences = React.lazy(
  () => import("pages/NotificationPreferences/NotificationPreferences")
);
const Onboardingv2 = React.lazy(() => import("pages/Onboardingv2"));
const PeopleImport = React.lazy(
  () => import("pages/PeopleImport/PeopleImport")
);
const PeopleSettings = React.lazy(
  () => import("pages/PeopleSettings/PeopleSettings")
);
const PeopleTablev2 = React.lazy(() => import("pages/PeopleTablev2"));
const Personv2 = React.lazy(() => import("pages/Personv2"));
const PosthogSettings = React.lazy(() => import("pages/PosthogSettings"));
const PushBuilder = React.lazy(() => import("pages/PushBuilder/PushBuilder"));
const PushSettings = React.lazy(() => import("pages/PushSettings"));
const ResetPassword = React.lazy(() => import("pages/ResetPassword"));
const Settingsv2 = React.lazy(() => import("pages/Settingsv2"));
const Signup = React.lazy(() => import("./pages/Signup"));
const SlackBuilder = React.lazy(() => import("pages/SlackBuilder"));
const SlackSettings = React.lazy(() => import("pages/SlackSettings"));
const SmsBuilder = React.lazy(() => import("pages/SmsBuilder"));
const SsoCallback = React.lazy(() => import("pages/SsoCallback"));
const SubscriptionPayment = React.lazy(
  () => import("pages/SubscriptionPayment/SubscriptionPayment")
);
const TelnyxSettings = React.lazy(() => import("pages/TelnyxSettings"));
const TemplateTablev2 = React.lazy(() => import("pages/TemplateTablev2"));
const TrackerTemplateBuilder = React.lazy(
  () => import("pages/TrackerTemplateBuilder")
);
const TrackerTemplateTable = React.lazy(
  () => import("pages/TrackerTemplateTable")
);
const TrainWorkflow = React.lazy(() => import("pages/TrainWorkflow"));
const TwilioSettings = React.lazy(() => import("pages/TwilioSettings"));
const Verificationv2 = React.lazy(() => import("pages/Verificationv2"));
const Verify = React.lazy(() => import("pages/Verify"));
const WebhookBuilder = React.lazy(() => import("pages/WebhookBuilder"));
const WorkflowTablev2 = React.lazy(() => import("pages/WorkflowTablev2"));

interface IProtected {
  children: ReactElement;
}

const Protected = ({ children }: IProtected) => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  useLayoutEffect(() => {
    const func = async () => {
      const loggedIn = await tokenService.verify();
      if (!loggedIn) {
        rememberPostLoginRedirect(
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );
        const internal = new URLSearchParams(window.location.search).get(
          "internal"
        );
        const leadstatsLoginUrl = AppConfig.LEADSTATS_LOGIN_URL;
        if (leadstatsLoginUrl && internal !== "1") {
          window.location.assign(
            buildLeadStatsMarketingLoginUrl(leadstatsLoginUrl)
          );
          return;
        }
        navigate("/login");
      }
      setIsLoggedIn(loggedIn);
    };
    func();
  }, []);

  const dispatch = useDispatch();
  if (isLoggedIn) {
    dispatch(getUserPermissions());
  }

  return isLoggedIn ? children : <></>;
};

interface IAllowed {
  route: string;
  children: ReactElement;
}

const Allowed = ({ route, children }: IAllowed) => {
  const navigate = useNavigate();
  const [isSlackAllowed, setIsSlackAllowed] = useState(true);
  const [isVerifiedAllowed, setIsVerifiedAllowed] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadData = async () => {
    try {
      const { data } = await ApiService.get({ url: "/allowed" });
      const { verified_not_allowed, slack_not_allowed } = data;
      setIsVerifiedAllowed(!verified_not_allowed);
      setIsSlackAllowed(!slack_not_allowed);
      setIsLoaded(true);
    } catch (e) {
      toast.error("Error while checking allowed routes");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (route === "verified") {
      if (isLoaded && !isVerifiedAllowed) navigate("/home");
    } else if (route === "slack") {
      if (isLoaded && !isSlackAllowed) navigate("/home");
    }
  }, [isVerifiedAllowed, isSlackAllowed, isLoaded]);

  if (route === "verified") {
    return isVerifiedAllowed ? <>{children}</> : <></>;
  }
  if (route === "slack") {
    return isSlackAllowed ? <>{children}</> : <></>;
  }
  return <></>;
};

interface VerificationProtectedProps {
  children: ReactNode;
}

const RouteFallback: FC = () => (
  <div className="min-h-screen w-full bg-themed-background" />
);

const VerificationProtected: FC<VerificationProtectedProps> = ({
  children,
}) => {
  const navigate = useNavigate();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isCompanySetuped, setIsCompanySetuped] = useState(false);
  const [isPlanActive, setIsPlanActive] = useState(false);

  const loadData = async () => {
    try {
      const { data } = await ApiService.get({ url: "/accounts" });
      const { verified, workspace } = data;
      setIsVerified(verified);
      setIsCompanySetuped(!!workspace?.id);
      const { isActive } = data;
      //console.log("here is the whole data", JSON.stringify(data, null, 2));
      setIsPlanActive(isActive);
      setIsLoaded(true);
    } catch (e) {
      toast.error("Error while loading data");
    }
  };

  /*
  const loadPlanStatus = async () => {
    try {
      const { data: planData } = await ApiService.get({
        url: "/accounts/check-active-plan",
      });
      console.log("after setup")
      console.log(JSON.stringify(planData, null, 2))
      console.log("plandata is a ", planData.isActive)
      setIsPlanActive(planData.isActive);
      console.log("isPlanActive is a", isPlanActive)
      console.log("is loaded");
    } catch (e) {
      toast.error("Error while loading data");
    }
  }
  */

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      if (!isVerified) {
        navigate("/verification");
      } else if (!isCompanySetuped) {
        navigate("/company-setup");
      } else if (!isPlanActive) {
        //console.log("am i getting here");
        //console.log(isPlanActive)
        navigate("/payment-gate");
      }
    }
  }, [isLoaded, isVerified, isCompanySetuped, isPlanActive]);

  return isVerified && isCompanySetuped ? <>{children}</> : <></>;
};

const RouteComponent: React.FC = () => {
  const [, setShowWelcomeBanner] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const isSsoCallbackRoute =
      window.location.pathname === "/auth/sso-callback";
    if (isSsoCallbackRoute || !tokenService.isUserLoggedIn()) {
      return;
    }

    // As soon as we're in the app authenticated (normal login or the Ricochet
    // Charts account-login / SSO entry), start warming the heavy list pages so
    // they're already cached by the time the user opens them.
    prefetchAppData(queryClient);

    (async () => {
      try {
        const { data } = await ApiService.get<Account>({ url: "/accounts" });

        // Update to info from organization
        dispatch({
          type: ActionType.LOGIN_USER_SUCCESS,
          payload: {
            firstName: data.firstName,
            lastName: data.lastName,
            uId: data.id,
            onboarded: data.onboarded,
            email: data.email,
            expectedOnboarding: data.expectedOnboarding,
            verified: data.verified,
            pk: data.workspace.pk,
            // pushPlatforms: data.workspace.pushPlatforms,
          },
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/login"
            element={<Login setShowWelcomeBanner={setShowWelcomeBanner} />}
          />
          <Route
            path="/signup"
            element={<Signup setShowWelcomeBanner={setShowWelcomeBanner} />}
          />
          <Route
            path="/signup/invitation/:id"
            element={
              <Signup fromInvite setShowWelcomeBanner={setShowWelcomeBanner} />
            }
          />
          <Route path="/confirm-invite/:id" element={<InviteConfirmation />} />
          <Route path="/auth/sso-callback" element={<SsoCallback />} />
          <Route
            path="/"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <Home />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/launch"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <CampaignLauncher />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/verify-email/:id"
            element={
              <Protected>
                <DrawerLayout>
                  <Verify />
                </DrawerLayout>
              </Protected>
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/:id" element={<ResetPassword />} />
          <Route path="/company-setup" element={<CompanySetup />} />
          <Route
            path="/verification"
            element={
              <Protected>
                <Verificationv2 />
              </Protected>
            }
          />
          <Route
            path="/notification-preferences"
            element={<NotificationPreferences />}
          />
          <Route
            path="/payment-gate"
            element={
              <Protected>
                <VerificationProtected>
                  <SubscriptionPayment />
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/flow"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[{ text: "Workflow builder", link: "/flow" }]}
                  >
                    <WorkflowTablev2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/flow/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Create a workflow" },
                    ]}
                  >
                    <FlowBuilderv2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/flow/:id/v2"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Create a workflow" },
                    ]}
                  >
                    <FlowBuilderv2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/event-tracker"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <EventTracker />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/example-workflows"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Example workflows" },
                    ]}
                  >
                    <ExampleWorkflows />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/flow/:id/view"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Workflow" },
                    ]}
                  >
                    <FlowViewerv2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/leadstats/automation-runs"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Automation queue" },
                    ]}
                  >
                    <LeadStatsAutomationQueue />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/train-workflow"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "Train Workflow" },
                    ]}
                  >
                    <TrainWorkflow />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/ai-agents"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Workflow builder", link: "/flow" },
                      { text: "AI Agents" },
                    ]}
                  >
                    <AiAgents />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/people"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <PeopleTablev2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/people/import"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Contacts", link: "/people" },
                      { text: "Import contacts" },
                    ]}
                  >
                    <PeopleImport />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/people/setting"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Contacts", link: "/people" },
                      { text: "Setting" },
                    ]}
                  >
                    <PeopleSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          {
            <Route
              path="/person/:id"
              element={
                <Protected>
                  <VerificationProtected>
                    <DrawerLayout
                      crumbs={[
                        { text: "Contacts", link: "/people" },
                        { text: "Current contact" },
                      ]}
                    >
                      <Personv2 />
                    </DrawerLayout>
                  </VerificationProtected>
                </Protected>
              }
            />
          }
          <Route
            path="/group"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <GroupTablev2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/group/create/automatic"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Groups", link: "/group" },
                      { text: "Create automatic group" },
                    ]}
                  >
                    <GroupCreation />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/group/create/manual"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Groups", link: "/group" },
                      { text: "Upload CSV" },
                    ]}
                  >
                    <ManualGroupCreator />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/group/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Groups", link: "/group" },
                      { text: "Group change" },
                    ]}
                  >
                    <GroupViewer />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/email-builder"
            element={<Navigate to="/templates" replace />}
          />
          <Route
            path="/slack-builder"
            element={<Navigate to="/templates" replace />}
          />
          <Route
            path="/sms-builder"
            element={<Navigate to="/templates" replace />}
          />
          <Route
            path="/templates/email/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <EmailBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates/slack/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <SlackBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates/sms/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Message Templates", link: "/templates" },
                      { text: "Create an SMS" },
                    ]}
                  >
                    <SmsBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates/push/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    crumbs={[
                      { text: "Message Templates", link: "/templates" },
                      { text: "Create a Push Notification" },
                    ]}
                  >
                    <PushBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates/webhook/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout
                    expandable
                    crumbs={[
                      { text: "Message Templates", link: "/templates" },
                      { text: "Create a webhook" },
                    ]}
                  >
                    <WebhookBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates/in-app/:id"
            element={
              <Protected>
                <VerificationProtected>
                  {/* <DrawerLayout
                  expandable
                  crumbs={[
                    { text: "Message Templates", link: "/templates" },
                    { text: "Create an In-App Message" },
                  ]}
                > */}
                  <InAppBackgroundProvider>
                    <InAppBuilder />
                  </InAppBackgroundProvider>
                  {/* </DrawerLayout> */}
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/templates"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TemplateTablev2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/tracker-template/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TrackerTemplateBuilder />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/tracker-template"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TrackerTemplateTable />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/data-transfer"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <DataTransferTable />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          {
            <Route
              path="/data-transfer/:id"
              element={
                <Protected>
                  <VerificationProtected>
                    <DrawerLayout>
                      <DataTransfer />
                    </DrawerLayout>
                  </VerificationProtected>
                </Protected>
              }
            />
          }

          {/* Removed for version 1 release
        <Route
          path="/integrations"
          element={
            <Protected>
              <VerificationProtected>
                <DrawerLayout>
                  <Integrations />
                </DrawerLayout>
              </VerificationProtected>
            </Protected>
          }
        />
        <Route
          path="/integrations/db"
          element={
            <Protected>
              <VerificationProtected>
                <DrawerLayout>
                  <Database />
                </DrawerLayout>
              </VerificationProtected>
            </Protected>
          }
        />
        <Route
          path="/integrations/db/:id"
          element={
            <Protected>
              <VerificationProtected>
                <DrawerLayout>
                  <Database />
                </DrawerLayout>
              </VerificationProtected>
            </Protected>
          }
        /> */}
          <Route
            path="/settings"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <Settingsv2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/email"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <EmailSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/email/:service/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <EmailSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/slack/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <SlackSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/add-member"
            element={
              <EEWrapper>
                <Protected>
                  <VerificationProtected>
                    <DrawerLayout>
                      <InviteMember />
                    </DrawerLayout>
                  </VerificationProtected>
                </Protected>
              </EEWrapper>
            }
          />
          <Route
            path="/settings/sms"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TwilioSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/sms/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TwilioSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/telnyx"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TelnyxSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/telnyx/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <TelnyxSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/push/:id"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <PushSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/custom-modal"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <CustomModalSettings />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/posthog"
            element={
              <Protected>
                <VerificationProtected>
                  <Navigate to="/settings" replace />
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/settings/javascript-snippet"
            element={
              <Protected>
                <VerificationProtected>
                  <Navigate to="/settings" replace />
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/home"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <Home />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/messages"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <Inbox />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/analytics"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <Analytics />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/onboarding"
            element={
              <Protected>
                <VerificationProtected>
                  <LaudspeakerProvider
                    apiKey={
                      config.get(ONBOARDING_API_KEY_KEY) ?? "onboarding-api-key"
                    }
                    apiHost={config.get(WS_BASE_URL_KEY)}
                  >
                    <Onboardingv2 />
                  </LaudspeakerProvider>
                </VerificationProtected>
              </Protected>
            }
          />
          <Route
            path="/slack/cor/:id"
            element={
              <Protected>
                <Allowed
                  route={"slack"}
                  children={
                    <VerificationProtected>
                      <Cor />
                    </VerificationProtected>
                  }
                />
              </Protected>
            }
          />
          {/*
        TEMP: uncomment in future
        <Route
          path="/workflows"
          element={
            <Protected>
              <Onboarded>
                <Workflows />
              </Onboarded>
            </Protected>
          }
        /> */}
          <Route
            path="*"
            element={
              <Protected>
                <VerificationProtected>
                  <DrawerLayout>
                    <WorkflowTablev2 />
                  </DrawerLayout>
                </VerificationProtected>
              </Protected>
            }
          />
        </Routes>
        <AiAssistantWidget />
      </Suspense>
    </BrowserRouter>
  );
};

export default RouteComponent;
